import { RefObject, useCallback, useState } from "react";
import type { MouseEvent } from "react";

import { Spinner } from "@/components/ui/spinner";
import { ConfirmModal } from "ui/confirm-required/confirm-modal";
import { User } from "../../../features/user";
import { ColCollaborator, ColComment } from "../types";
import { CollaborationVisibility } from "./constants";
import { CollaborationInputControlsV2 } from "./CollaborationInputControlsV2";
import CollaborationMessage from "./CollaborationMessage";

export type CollaborationChatViewVariant = "modal" | "drawer";

export interface CollaborationChatViewAdapter {
  collaborators: ColCollaborator[];
  comments: ColComment[];
  currentUser: User;
  disabled?: boolean;
  editingComment: ColComment | null;
  isError: boolean;
  isLoading: boolean;
  messagesContainerRef: RefObject<HTMLDivElement>;
  postState: string;
  showEmptyState: boolean;
  onCancelEdit: () => void;
  onClearError: () => void;
  onDeleteComment: (comment: ColComment) => Promise<void> | void;
  onMessageSend: (
    text: string,
    visibility: CollaborationVisibility
  ) => Promise<boolean>;
  onStartEdit: (comment: ColComment) => void;
}

export interface CollaborationChatViewProps extends CollaborationChatViewAdapter {
  messagesTestId?: string;
  resetKey?: string;
  variant?: CollaborationChatViewVariant;
}

interface ActiveThreadListProps {
  comments: ColComment[];
  collaborators: ColCollaborator[];
  currentUser: User;
  editingCommentId?: string;
  onDelete: (comment: ColComment) => void;
  onEdit: (comment: ColComment) => void;
  postState: string;
  disabled?: boolean;
}

const ActiveThreadList = ({
  comments,
  collaborators,
  currentUser,
  editingCommentId,
  onDelete,
  onEdit,
  postState,
  disabled,
}: ActiveThreadListProps) => {
  if (comments.length === 0) {
    return null;
  }

  return (
    <>
      {comments.map((comment, index) => (
        <CollaborationMessage
          key={comment._id}
          comment={comment}
          previousComment={index > 0 ? comments[index - 1] : undefined}
          nextComment={
            index < comments.length - 1 ? comments[index + 1] : undefined
          }
          collaborators={collaborators}
          currentUser={currentUser}
          onEdit={onEdit}
          onDelete={onDelete}
          isEditing={editingCommentId === comment._id}
          postState={postState}
          disabled={disabled}
        />
      ))}
    </>
  );
};

export function CollaborationChatView({
  collaborators,
  comments,
  currentUser,
  disabled,
  editingComment,
  isError,
  isLoading,
  messagesContainerRef,
  messagesTestId,
  onCancelEdit,
  onClearError,
  onDeleteComment,
  onMessageSend,
  onStartEdit,
  postState,
  resetKey,
  showEmptyState,
  variant = "modal",
}: CollaborationChatViewProps) {
  const [commentPendingDeletionState, setCommentPendingDeletionState] =
    useState<{ comment: ColComment; resetKey?: string } | null>(null);
  const commentPendingDeletion =
    commentPendingDeletionState &&
    commentPendingDeletionState.resetKey === resetKey
      ? commentPendingDeletionState.comment
      : null;

  const requestDelete = useCallback(
    (comment: ColComment) => {
      setCommentPendingDeletionState({ comment, resetKey });
    },
    [resetKey]
  );

  const handleCancelDelete = useCallback(
    (event?: MouseEvent<HTMLButtonElement>) => {
      event?.preventDefault();
      setCommentPendingDeletionState(null);
    },
    []
  );

  const handleConfirmDelete = useCallback(() => {
    if (!commentPendingDeletion) {
      return;
    }

    void Promise.resolve(onDeleteComment(commentPendingDeletion))
      .catch(() => {})
      .finally(() => {
        setCommentPendingDeletionState(null);
      });
  }, [commentPendingDeletion, onDeleteComment]);

  const hasCollaborators = collaborators.length > 0;
  const hasThreads = comments.length > 0;

  const isDeletingExternal = commentPendingDeletion?.visibility === "EXTERNAL";
  const deleteCopy = isDeletingExternal
    ? {
        title: "Delete external note",
        message:
          "This action cannot be undone. Once deleted, this external note will be permanently removed from the conversation.",
        confirm: "Delete external note",
      }
    : {
        title: "Delete internal comment",
        message:
          "This action cannot be undone. Once deleted, this internal comment will be permanently removed from the conversation.",
        confirm: "Delete internal comment",
      };

  const containerClassName =
    variant === "drawer"
      ? "relative flex h-full min-w-0 flex-col"
      : "min-w-400px max-w-400px relative flex h-[620px] max-h-[620px] flex-col";
  const listClassName =
    variant === "drawer"
      ? "relative flex-1 overflow-y-auto rounded-[8px] border border-neutral-100 bg-white"
      : "relative flex flex-1 flex-col overflow-y-auto rounded-[8px] border border-solid border-[#e6e6e6] bg-white py-4";

  return (
    <div className={containerClassName}>
      {isLoading && (
        <div className="absolute inset-x-0 top-0 bottom-20 z-[3] flex items-center justify-center bg-white/75">
          <Spinner size="xl" />
        </div>
      )}

      <div
        ref={messagesContainerRef}
        data-testid={messagesTestId}
        className={listClassName}
      >
        {hasCollaborators && hasThreads && (
          <ActiveThreadList
            comments={comments}
            collaborators={collaborators}
            currentUser={currentUser}
            editingCommentId={editingComment?._id}
            onDelete={requestDelete}
            onEdit={onStartEdit}
            postState={postState}
            disabled={disabled}
          />
        )}

        {showEmptyState &&
          (variant === "drawer" ? (
            <div className="absolute bottom-8 left-4 right-4 rounded-[10px] bg-[#f5f3ff] px-4 py-3 text-center">
              <h4 className="m-0 font-inter text-sm font-semibold text-neutral-800">
                Keep the Conversation Flowing
              </h4>
              <p className="m-0 mt-1 font-inter text-xs text-neutral-600">
                This is where internal comments, external notes, and status
                updates live. Add one below to get the conversation started!
              </p>
            </div>
          ) : (
            <div className="mt-auto flex w-full flex-col items-center self-center px-4 text-center">
              <h3 className="m-0 text-center text-base font-semibold leading-6 text-[#0e1680]">
                Keep the Conversation Flowing
              </h3>
              <p className="mx-0 mt-2 mb-4 text-center font-inter text-sm font-normal leading-5 text-[#333]">
                This is where internal comments, external notes, and status
                updates live. Add one below to get the conversation started!
              </p>
              <div aria-hidden="true">
                <i className="fa-regular fa-chevron-down text-[14px] color-[#4f31ff]" />
              </div>
            </div>
          ))}
      </div>

      <CollaborationInputControlsV2
        collaborators={collaborators}
        onMessageSend={onMessageSend}
        editingComment={editingComment}
        disabled={disabled}
        currentUser={currentUser}
        onCancelEdit={onCancelEdit}
        isError={isError}
        onClearError={onClearError}
      />

      <ConfirmModal
        maxWidth="552px"
        show={Boolean(commentPendingDeletion)}
        title={deleteCopy.title}
        message={deleteCopy.message}
        confirmText={deleteCopy.confirm}
        cancelText="Cancel"
        confirmType={{ alert: true }}
        onCancel={(event) => handleCancelDelete(event)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
